import React from 'react'
import styled from 'styled-components'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { Link } from 'react-router-dom'
import { 
  FiDroplet, 
  FiThermometer, 
  FiTool, 
  FiClock, 
  FiPhone, 
  FiCheckCircle,
  FiArrowRight
} from 'react-icons/fi'

const PageContainer = styled.div`
  padding-top: 80px; /* Account for fixed navbar */
`

const HeroSection = styled.section`
  background: var(--gradient-hero);
  color: white;
  padding: 8rem 0 6rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('/images/81a57df0-32c8-4143-8628-83c07947047d.png') center/cover;
    opacity: 0.1;
    z-index: 0;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    right: 0;
    height: 120px;
    background: var(--color-bg);
    clip-path: polygon(0 60%, 100% 0, 100% 100%, 0 100%);
    z-index: 1;
  }
`

const HeroContent = styled.div`
  max-width: var(--container-max-width);
  margin: 0 auto;
  padding: 0 var(--container-padding);
  position: relative;
  z-index: 1;
  text-align: center;
`

const HeroTitle = styled(motion.h1)`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  line-height: 1.1;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }

  @media (max-width: 480px) {
    font-size: 2rem;
  }
`

const HeroSubtitle = styled(motion.p)`
  font-size: 1.25rem;
  margin-bottom: 3rem;
  opacity: 0.9;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    font-size: 1.125rem;
  }
`

const HeroButtons = styled(motion.div)`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    
    .btn {
      width: 100%;
      max-width: 280px;
    }
  }
`

const ServicesSection = styled.section`
  padding: 6rem 0;
  background: var(--color-bg);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 10%;
    right: 10%;
    height: 1px;
    background: linear-gradient(90deg, transparent, var(--color-border), transparent);
  }
`

const SectionTitle = styled(motion.h2)`
  text-align: center;
  margin-bottom: 3rem;
  font-size: 2.5rem;
  color: var(--color-text);
`

const ServicesGrid = styled.div`
  max-width: var(--container-max-width);
  margin: 0 auto;
  padding: 0 var(--container-padding);
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
`

const ServiceCard = styled(motion.div)`
  background: var(--gradient-card);
  padding: 2.5rem;
  border-radius: var(--border-radius-xl);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  text-align: center;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(59, 130, 246, 0.2);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: var(--gradient-accent);
    transform: translateY(-100%);
    transition: transform 0.3s ease;
  }

  &::after {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 60px rgba(37, 99, 235, 0.4);
    border-color: rgba(59, 130, 246, 0.4);

    &::before {
      transform: translateY(0);
    }

    &::after {
      opacity: 1;
    }
  }
`

const ServiceImage = styled.div`
  width: 120px;
  height: 120px;
  margin: 0 auto 1.5rem;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  position: relative;
  background: var(--gradient-accent);
  padding: 3px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 17px;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.05);
  }
`

const ServiceTitle = styled.h3`
  margin-bottom: 1rem;
  color: var(--color-text);
`

const ServiceDescription = styled.p`
  color: var(--color-text-light);
  line-height: 1.6;
`

const WhyChooseSection = styled.section`
  padding: 6rem 0;
  background: var(--gradient-dark);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 20%;
    right: -10%;
    width: 400px;
    height: 400px;
    background: radial-gradient(circle, rgba(59, 130, 246, 0.2) 0%, transparent 70%);
    border-radius: 50%;
    z-index: 0;
  }
`

const WhyChooseContent = styled.div`
  max-width: var(--container-max-width);
  margin: 0 auto;
  padding: 0 var(--container-padding);
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`

const WhyChooseText = styled.div`
  h2 {
    font-size: 2.5rem;
    margin-bottom: 1.5rem;
    color: var(--color-text);
  }

  p {
    font-size: 1.125rem;
    margin-bottom: 2rem;
    color: var(--color-text-light);
  }
`

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 2rem;
`

const FeatureItem = styled(motion.li)`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.25rem;
  font-weight: 600;
  color: var(--color-text);
  padding: 0.75rem 1rem;
  background: rgba(30, 41, 59, 0.8);
  border-radius: var(--border-radius);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  border: 1px solid rgba(59, 130, 246, 0.2);

  &:hover {
    transform: translateX(8px);
    box-shadow: 0 8px 24px rgba(59, 130, 246, 0.3);
    background: rgba(51, 65, 85, 0.9);
    border-color: rgba(59, 130, 246, 0.4);
  }

  svg {
    color: var(--color-success);
    flex-shrink: 0;
    background: rgba(16, 185, 129, 0.2);
    padding: 0.25rem;
    border-radius: 50%;
  }
`

const WhyChooseImage = styled.div`
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: -20px;
    left: -20px;
    right: 20px;
    bottom: 20px;
    background: var(--gradient-accent);
    border-radius: var(--border-radius-xl);
    z-index: -1;
    opacity: 0.3;
  }

  img {
    width: 100%;
    height: 400px;
    object-fit: cover;
    border-radius: var(--border-radius-xl);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.02);
  }
`

const CTASection = styled.section`
  background: var(--gradient-dark);
  color: white;
  padding: 6rem 0;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 50%);
    animation: pulse 4s ease-in-out infinite;
  }

  @keyframes pulse {
    0%, 100% { transform: scale(1); opacity: 0.5; }
    50% { transform: scale(1.1); opacity: 0.3; }
  }
`

const CTAContent = styled.div`
  max-width: var(--container-max-width);
  margin: 0 auto;
  padding: 0 var(--container-padding);
`

const CTATitle = styled(motion.h2)`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
`

const CTASubtitle = styled(motion.p)`
  font-size: 1.25rem;
  margin-bottom: 3rem;
  opacity: 0.9;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`

const CTAButtons = styled(motion.div)`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    
    .btn {
      width: 100%;
      max-width: 280px;
    }
  }
`

const HomePage: React.FC = () => {
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 })
  const [servicesRef, servicesInView] = useInView({ triggerOnce: true, threshold: 0.1 })
  const [whyChooseRef, whyChooseInView] = useInView({ triggerOnce: true, threshold: 0.1 })
  const [ctaRef, ctaInView] = useInView({ triggerOnce: true, threshold: 0.1 })

  const services = [
    {
      image: '/images/81a57df0-32c8-4143-8628-83c07947047d.png',
      title: 'Instalacje hydrauliczne',
      description: 'Naprawy i wymiany instalacji wod-kan, armatury, baterii. Profesjonalne podejście do każdego zlecenia.'
    },
    {
      image: '/images/be580015-4c0d-4fa3-b2b6-3ba7a3441b5a.png',
      title: 'Instalacje grzewcze',
      description: 'Serwis systemów C.O., wymiana grzejników, naprawy kotłów. Zapewniamy ciepło w Twoim obiekcie.'
    },
    {
      image: '/images/4cb98481-bb7f-4e6f-9548-66f6f6004bdf.png',
      title: 'Ślusarka i remonty',
      description: 'Naprawy drzwi, bram przemysłowych, drobne prace remontowe. Kompleksowe rozwiązania techniczne.'
    },
    {
      image: '/images/dd6c30f4-edbc-43bc-9dc6-45ca4f31729c.png',
      title: 'Zaplanowane serwisy',
      description: 'Regularne przeglądy i konserwacje. Planujemy prace w dogodnym dla Ciebie terminie.'
    }
  ]

  const features = [
    'Doświadczenie w obsłudze firm B2B',
    'Szybka reakcja na zgłoszenia',
    'Profesjonalny sprzęt i narzędzia',
    'Konkurencyjne ceny',
    'Terminowe wykonanie prac',
    'Obsługa całego Trójmiasta'
  ]

  return (
    <PageContainer>
      <HeroSection ref={heroRef}>
        <HeroContent>
          <HeroTitle
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            Profesjonalne usługi serwisowe dla firm
          </HeroTitle>
          <HeroSubtitle
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Instalacje hydrauliczne, grzewcze, ślusarka i zaplanowane prace serwisowe. 
            Obsługujemy firmy w całym Trójmieście.
          </HeroSubtitle>
          <HeroButtons
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <Link to="/kontakt" className="btn btn-white btn-lg">
              Uzyskaj wycenę
              <FiArrowRight style={{ marginLeft: '0.5rem' }} />
            </Link>
            <a href="tel:510184702" className="btn btn-secondary btn-lg">
              <FiPhone />
              510 184 702
            </a>
          </HeroButtons>
        </HeroContent>
      </HeroSection>

      <ServicesSection ref={servicesRef}>
        <SectionTitle
          initial={{ opacity: 0, y: 30 }}
          animate={servicesInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          Nasze usługi
        </SectionTitle>
        <ServicesGrid>
          {services.map((service, index) => (
            <ServiceCard
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={servicesInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: index * 0.1 }}
            >
              <ServiceImage>
                <img src={service.image} alt={service.title} />
              </ServiceImage>
              <ServiceTitle>{service.title}</ServiceTitle>
              <ServiceDescription>{service.description}</ServiceDescription>
            </ServiceCard>
          ))}
        </ServicesGrid>
      </ServicesSection>

      <WhyChooseSection ref={whyChooseRef}>
        <WhyChooseContent>
          <WhyChooseText>
            <motion.h2
              initial={{ opacity: 0, x: -30 }}
              animate={whyChooseInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              Dlaczego FachFIX?
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, x: -30 }}
              animate={whyChooseInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Jesteśmy specjalistami w obsłudze firm. Rozumiemy specyfikę pracy 
              w środowisku biznesowym i dostosowujemy nasze usługi do Twoich potrzeb.
            </motion.p>
            <FeaturesList>
              {features.map((feature, index) => (
                <FeatureItem
                  key={index}
                  initial={{ opacity: 0, x: -30 }}
                  animate={whyChooseInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.8, delay: 0.3 + index * 0.1 }}
                >
                  <FiCheckCircle />
                  {feature}
                </FeatureItem>
              ))}
            </FeaturesList>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={whyChooseInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <Link to="/o-nas" className="btn btn-primary">
                Poznaj nas lepiej
                <FiArrowRight style={{ marginLeft: '0.5rem' }} />
              </Link>
            </motion.div>
          </WhyChooseText>
          <WhyChooseImage>
            <motion.img
              src="/images/7ff4d01b-d9fd-4d11-9f36-603bf177b279.png"
              alt="Profesjonalny serwis FachFIX"
              initial={{ opacity: 0, x: 30 }}
              animate={whyChooseInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4 }}
            />
          </WhyChooseImage>
        </WhyChooseContent>
      </WhyChooseSection>

      <CTASection ref={ctaRef}>
        <CTAContent>
          <CTATitle
            initial={{ opacity: 0, y: 30 }}
            animate={ctaInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            Potrzebujesz pomocy technicznej?
          </CTATitle>
          <CTASubtitle
            initial={{ opacity: 0, y: 30 }}
            animate={ctaInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Skontaktuj się z nami już dziś i otrzymaj bezpłatną wycenę. 
            Zaplanujemy prace w dogodnym dla Ciebie terminie.
          </CTASubtitle>
          <CTAButtons
            initial={{ opacity: 0, y: 30 }}
            animate={ctaInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <Link to="/kontakt" className="btn btn-white btn-lg">
              Skontaktuj się z nami
            </Link>
            <a href="tel:510184702" className="btn btn-secondary btn-lg">
              <FiPhone />
              Zadzwoń teraz
            </a>
          </CTAButtons>
        </CTAContent>
      </CTASection>
    </PageContainer>
  )
}

export default HomePage 